import React, { useState, useEffect } from "react";
import {
    Card,
    Form,
    Input,
    Button,
    DatePicker,
    Switch,
    message,
    Breadcrumb,
    Divider,
    Row,
    Col,
    Spin,
    InputNumber,
    Select,
    Space,
    Popconfirm,
    Typography,
    Modal,
    TimePicker,
    TreeSelect,
    Alert,
    Popover,
} from "antd";
import {
    SaveOutlined,
    ArrowLeftOutlined,
    HomeOutlined,
    UserOutlined,
    PlusOutlined,
    DeleteOutlined,
    CalendarOutlined,
    EnvironmentOutlined,
    DollarOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    MessageOutlined,
    SearchOutlined,
    FilterOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { FixedSizeList as List } from 'react-window';
import { campainAPIWithErrorHandling, campainHelpers } from "../../../api/collection/campain";
import { groupCustomerAPIWithErrorHandling } from "../../../api/groupCustomer/groupCustomer";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import SlateEditor from "../../../components/Editor/SlateEditor";
import { useSelector } from "react-redux";
import { paymentAccountAPIWithErrorHandling } from "../../../api/payment/paymentAccount";
import PaymentAccountSelector from "../PaymentAccount/PaymentAccountList";
import { cnfAPIWithErrorHandling } from "../../../api/invoice/cnf";

const { Text } = Typography;
const { RangePicker } = DatePicker;

export default function UpdateCampain() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [loadingCus, setLoadingCus] = useState(false);
    const [loadingGroup, setLoadingGroup] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [availableCustomers, setAvailableCustomers] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [availableGroups, setAvailableGroups] = useState([]);
    const [valueOrg, setValueOrg] = useState();
    const [treeData, setTreeData] = useState([]);
    const [valueSendScheduleAt, setValueSendScheduleAt] = useState(null);
    const [contentValue, setContentValue] = useState('');
    const [campainData, setCampainData] = useState(null);
      
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const isSendZns = Form.useWatch("is_send_zns", form);
      
    const [filterOrgId, setFilterOrgId] = useState(null);
    const [filterText, setFilterText] = useState("");
    const [sortByOrg, setSortByOrg] = useState(true); // Mặc định bật để show grouped
    
    const [invoiceCnfs, setInvoiceCnfs] = useState([]);

    const { user } = useSelector(
        (state) => state.persistedReducer.user
    );
    const userType = user?.data.info.type;

    useEffect(() => {
        fetchAccount();
        loadOrganizations();
        loadCampainData();
        fetchInvoiceCnf();
    }, [id]);

    useEffect(() => {
        if (valueOrg) {
            fetchGroupCustomer(valueOrg);
            fetchAvailableCustomers(valueOrg, null);
        }
    }, [valueOrg]);
        
    const fetchInvoiceCnf = async () => {
        try {
            const response = await cnfAPIWithErrorHandling.getCnfAll();
            const icfData = response?.data?.data || [];
            setInvoiceCnfs(icfData);
        } catch (error) {
            message.error("Không thể tải danh sách tài khoản xuất hóa đơn: " + error.message);
        }
    };
      
    const fetchAccount = async () => {
        try {
            const response = await paymentAccountAPIWithErrorHandling.getAccountAll();
            const groupData = response?.data || [];
            setAccounts(groupData);
            setSelectedAccount(null);
        } catch (error) {
            message.error("Không thể tải danh sách tài khoản: " + error.message);
        }
    };
    
    const handleSelectAccount = (account) => {
        setSelectedAccount(account);
        message.success(`Đã chọn tài khoản: ${account.name}`);
    };

    const onChangeSendScheduleAt = time => {
        setValueSendScheduleAt(time);
    };

    const loadCampainData = async () => {
        try {
            setLoading(true);
            const response = await campainAPIWithErrorHandling.getCampainById(id);
            const data = response.data.data;
            
            if (!data) {
                message.error("Không tìm thấy chiến dịch!");
                navigate("/campains");
                return;
            }

            if(data && (userType === 'STAFF' || userType === 'BRANCH_MANAGER') && data.is_locked === 1){
                message.error("Bạn không có quyền chỉnh sửa chiến dịch này!");
                navigate(`/campains/information/${id}`);
                return;
            }

            setCampainData(data);

            // Set form values
            form.setFieldsValue({
                name: data.name,
                dateRange: [dayjs(data.start_date), dayjs(data.end_date)],
                status: data.status,
                is_send_zns: data.is_send_zns,
                zns_oa_name: data.zns_oa_name,
                zns_template_id: data.zns_template_id,
                before_date: data.before_date,
                after_date: data.after_date,
                content: data.content,
                send_schedule_at: data.send_schedule_at ? dayjs(data.send_schedule_at, "HH:mm:ss") : null,
                invoice_cnf_id: data.invoice_cnf_id ?? null,
            });

            if (data.send_schedule_at) {
                const time = dayjs(data.send_schedule_at, "HH:mm:ss");
                setValueSendScheduleAt(time);
            }

            if (data.content) {
                setContentValue(data.content);
            }

            let campainCusFees = [];
            if (data.type_child === 'PARENT' && data.children?.length > 0) {
                data.children.forEach(child => {
                    campainCusFees = campainCusFees.concat(child.customer_fees || []);
                });
            } else {
                campainCusFees = data.customer_fees || [];
            }

            // Load customers
            const customersList = campainCusFees.map(cf => ({
                id: cf.id,
                customer_id: cf.customer.id,
                customer_name: cf.customer.name,
                customer_phone: cf.customer.phone,
                customer_code: cf.customer.code,
                dob: cf.customer.dob,
                organization_id: cf.collection.organization_id,
                acc_payment_id: cf.acc_payment_id || "",
            }));
            setCustomers(customersList);

            message.success("Tải dữ liệu chiến dịch thành công!");
        } catch (error) {
            message.error("Không thể tải dữ liệu chiến dịch: " + error.message);
            navigate("/campains");
        } finally {
            setLoading(false);
        }
    };

    const fetchGroupCustomer = async (orgId) => {
        try {
            setLoadingGroup(true);
            const response = await groupCustomerAPIWithErrorHandling.getGroupAllByOrg(orgId);
            const groupData = response?.data || [];
            setAvailableGroups(groupData);
            setSelectedGroup(null);
        } catch (error) {
            message.error("Không thể tải danh sách nhóm khách hàng: " + error.message);
        } finally {
            setLoadingGroup(false);
        }
    };

    const fetchAvailableCustomers = async (orgId, groupId = null) => {
        try {
            setLoadingCus(true);
            let customersData = [];
            if (groupId) {
                const response = await groupCustomerAPIWithErrorHandling.getCustomerAllByGroup(groupId);
                customersData = response.data || [];
            } else {
                const response = await organizationAPIWithErrorHandling.getCustomerAllByOrg(orgId);
                customersData = response.data || [];
            }
            setAvailableCustomers(customersData);
        } catch (error) {
            message.error("Không thể tải danh sách khách hàng: " + error.message);
        } finally {
            setLoadingCus(false);
        }
    };

    const onChange = (newValOrg) => {
        setValueOrg(newValOrg);
        setAvailableCustomers([]);
        setSelectedGroup(null);
    };

    const handleGroupChange = (groupId) => {
        setSelectedGroup(groupId);
        if (groupId) {
            fetchAvailableCustomers(valueOrg, groupId);
        } else {
            fetchAvailableCustomers(valueOrg, null);
        }
    };

    const loadOrganizations = async () => {
        try {
            const response = await organizationAPIWithErrorHandling.getTreeStructure();
            if (response.success) {
                const rawData = response.data;
                const flatTree = flattenTreeData(rawData);
                setTreeData(flatTree);
            }
        } catch (error) {
            message.error(error.message);
        }
    };

    const flattenTreeData = (nodes, parentId = 0) => {
        let result = [];
        nodes.forEach((node) => {
            result.push({
                id: node.id,
                pId: parentId,
                value: String(node.id),
                title: node.name,
                isLeaf: node.children_recursive?.length === 0,
            });
            if (node.children_recursive && node.children_recursive.length > 0) {
                result = result.concat(flattenTreeData(node.children_recursive, node.id));
            }
        });
        return result;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString("vi-VN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) {
            const startDate = dates[0];
            const month = startDate.month() + 1;
            const year = startDate.year();
            const suggestedName = campainHelpers.generateCampainName(month, year);
            const checkName = form.getFieldValue("name");
            if (!checkName) {
                form.setFieldValue("name", suggestedName);
            }
        }
    };

    const addCustomer = (customerId) => {
        if (!customerId) {
            message.warning("Vui lòng chọn khách hàng");
            return;
        }
                
        if (!selectedAccount) {
            message.warning("Vui lòng chọn tài khoản thanh toán trước");
            return;
        }

        const customer = availableCustomers.find(c => c.id === customerId);
        if (!customer) {
            message.error("Không tìm thấy khách hàng");
            return;
        }

        if (customers.some(c => c.customer_id === customerId)) {
            message.warning("Khách hàng này đã được thêm");
            return;
        }

        const newCustomer = {
            customer_id: customerId,
            customer_name: customer.name,
            customer_phone: customer.phone,
            customer_code: customer.code ?? null,
            dob: customer.dob,
            organization_id: valueOrg ?? null,
            acc_payment_id: selectedAccount.id
        };

        setCustomers([...customers, newCustomer]);

        const currentData = form.getFieldValue("customerData") || {};
        form.setFieldValue("customerData", {
            ...currentData,
            [customerId]: {
                acc_payment_id: selectedAccount.id
            }
        });

        message.info(`Đã thêm khách hàng: ${customer.name}`);
    };

    const addAllCustomers = () => {
        if (availableCustomers.length === 0) {
            message.warning("Không có khách hàng nào để thêm");
            return;
        }
            
        if (!selectedAccount) {
            message.warning("Vui lòng chọn tài khoản thanh toán trước");
            return;
        }

        const notAddedCustomers = availableCustomers.filter(
            (customer) => !customers.some((c) => c.customer_id === customer.id)
        );

        if (notAddedCustomers.length === 0) {
            message.warning("Tất cả khách hàng đã được thêm rồi");
            return;
        }

        const newCustomers = notAddedCustomers.map((customer) => ({
            customer_id: customer.id,
            customer_name: customer.name,
            customer_phone: customer.phone,
            customer_code: customer.code,
            dob: customer.dob,
            organization_id: valueOrg ?? null,
            acc_payment_id: selectedAccount.id
        }));

        setCustomers((prev) => [...prev, ...newCustomers]);

        form.setFieldValue("customerData", newCustomers);

        message.success(`Đã thêm ${notAddedCustomers.length} khách hàng`);
    };

    const removeCustomer = (customerId) => {
        setCustomers((prev) => prev.filter((c) => c.customer_id !== customerId));
        message.info("Đã xóa khách hàng khỏi chiến dịch");
    };

    const columns = [
        {
            title: "Mã KH",
            dataIndex: "customer_code",
            key: "customer_code",
            width: 250,
            render: (text) => <strong className="text-blue-600">{text}</strong>,
        },
        {
            title: "Khách hàng",
            dataIndex: "customer_name",
            key: "customer_name",
            width: 250,
            render: (text, record) => {
                const customerInfoContent = (
                    <div style={{ maxWidth: 320 }}>
                        <div style={{ marginBottom: 12 }}>
                            <Text strong style={{ fontSize: 16 }}>{record.customer_name}</Text>
                        </div>
                        <Divider style={{ margin: '8px 0' }} />
                        <div style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Số điện thoại:</Text>
                            <div><Text strong>{record.customer_phone}</Text></div>
                        </div>
                        <Divider style={{ margin: '8px 0' }} />
                        <Button
                            type="link"
                            size="small"
                            icon={<UserOutlined />}
                            style={{ padding: 0 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                Modal.confirm({
                                    title: "Xem chi tiết khách hàng",
                                    icon: <ExclamationCircleOutlined />,
                                    content: `Bạn có muốn chuyển đến trang chi tiết của khách hàng "${record.customer_name}" không?`,
                                    okText: "Xem chi tiết",
                                    cancelText: "Hủy",
                                    onOk: () => {
                                        navigate(`/customers/information/${record.customer_id}`);
                                    },
                                });
                            }}
                        >
                            Xem chi tiết khách hàng
                        </Button>
                    </div>
                );

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                        <div style={{ flex: 1 }}>
                            <div className="font-medium">{text}</div>
                        </div>
                        <Popover
                            content={customerInfoContent}
                            title={null}
                            trigger="hover"
                            placement="rightTop"
                            overlayStyle={{ maxWidth: 350 }}
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={<InfoCircleOutlined />}
                                style={{ color: '#0070D7', padding: 4, height: 'auto' }}
                            />
                        </Popover>
                    </div>
                );
            },
        },
        ...(!sortByOrg ?
            [
                {
                    title: "Đơn vị",
                    dataIndex: "org_id",
                    key: "org_id",
                    width: 200,
                    render: (text, record) => {
                        const orgItem = treeData.find(c => Number(c.id) === Number(record.organization_id));
                        if (!orgItem) {
                            return <Text type="secondary">Không xác định</Text>;
                        }
                        
                        return (
                            <Popover
                                content={
                                    <div>
                                        <div><Text strong>ID:</Text> {orgItem.id}</div>
                                        <div><Text strong>Tên:</Text> {orgItem.title}</div>
                                    </div>
                                }
                                title="Thông tin đơn vị"
                                trigger="hover"
                            >
                                <Text style={{ cursor: 'pointer' }}>
                                    {orgItem.title}
                                </Text>
                            </Popover>
                        );
                    }
                },
            ] : []
        ),
        {
            title: "Điện thoại",
            dataIndex: "customer_phone",
            key: "customer_phone",
            width: 250,
        },
        {
            title: "Ngày sinh",
            dataIndex: "dob",
            key: "dob",
            width: 250,
            render: (date) => formatDate(date),
        },
        {
            title: "Tài khoản",
            dataIndex: "acc_payment_id",
            key: "acc_payment_id",
            width: 320,
            render: (_, record) => {
            // const formValue = form.getFieldValue(["customerData", record.customer_id, "acc_payment_id"]);
            // const currentAccountId = formValue || record.acc_payment_id;
            // const currentAccount = accounts.find(acc => acc.id === currentAccountId);
            
            return (
                <Form.Item
                    name={["customerData", record.customer_id, "acc_payment_id"]}
                    style={{ margin: 0, width: '100%' }}
                    rules={[{ required: true, message: "Chọn tài khoản" }]}
                    initialValue={record.acc_payment_id}
                >
                <Select
                    style={{ width: "100%" }}
                    // placeholder={currentAccount ? currentAccount.name : "Chọn tài khoản"}
                    placeholder="Chọn tài khoản"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={accounts.map((item) => ({
                        value: item.id,
                        label: item.name,
                    }))}
                    // THÊM onChange để cập nhật state customers
                    onChange={(value) => {
                        setCustomers(prev => 
                            prev.map(c => 
                                c.customer_id === record.customer_id 
                                    ? { ...c, acc_payment_id: value }
                                    : c
                            )
                        );
                    }}
                />
                </Form.Item>
            );
            },
        },
        {
            title: "Thao tác",
            key: "action",
            width: 200,
            align: 'center',
            render: (_, record) => (
                <Popconfirm
                    title="Xóa khách hàng?"
                    description="Xóa khách hàng khỏi chiến dịch này"
                    onConfirm={() => removeCustomer(record.customer_id)}
                    okText="Xóa"
                    cancelText="Hủy"
                >
                    <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                </Popconfirm>
            ),
        },
    ];

    // const tableDataSource = customers.map(customer => ({
    //     key: customer.customer_id,
    //     ...customer,
    // }));
    // Xử lý lọc và sắp xếp VỚI GROUPED
    const tableDataSource = React.useMemo(() => {
        let data = customers.map(customer => ({
            key: customer.customer_id,
            ...customer,
        }));
    
        // Lọc theo đơn vị
        if (filterOrgId) {
            data = data.filter(item => Number(item.organization_id) === Number(filterOrgId));
        }
    
        // Lọc theo tên hoặc số điện thoại
        if (filterText.trim()) {
            const searchText = filterText.toLowerCase().trim();
            data = data.filter(item => 
            item.customer_name?.toLowerCase().includes(searchText) ||
            item.customer_phone?.toLowerCase().includes(searchText)
            );
        }
    
        // ✅ NẾU BẬT SẮP XẾP -> NHÓM THEO ĐơN VỊ
        if (sortByOrg) {
            // Nhóm entries theo organization_id
            const grouped = data.reduce((acc, entry) => {
            const orgId = entry.organization_id || 'unknown';
            if (!acc[orgId]) {
                acc[orgId] = [];
            }
            acc[orgId].push(entry);
            return acc;
            }, {});
            
            // Sắp xếp các nhóm theo tên đơn vị
            const sortedGroups = Object.entries(grouped).sort(([orgIdA], [orgIdB]) => {
            const orgA = treeData.find(c => Number(c.id) === Number(orgIdA));
            const orgB = treeData.find(c => Number(c.id) === Number(orgIdB));
            
            const nameA = orgA?.title || 'Không xác định';
            const nameB = orgB?.title || 'Không xác định';
            
            return nameA.localeCompare(nameB, 'vi');
            });
            
            // Flatten lại với header cho mỗi nhóm
            const result = [];
            sortedGroups.forEach(([orgId, items]) => {
            const orgItem = treeData.find(c => Number(c.id) === Number(orgId));
            
            // Thêm header row
            result.push({
            key: `header_${orgId}`,
            isHeader: true,
            orgName: orgItem?.title || 'Không xác định',
            itemCount: items.length
            });
            
            // Thêm các items
            result.push(...items);
            });
            
            return result;
        }
    
        // ✅ NẾU TẮT SẮP XẾP -> TRẢ VỀ DANH SÁCH PHẲNG
        return data;
    }, [customers, filterOrgId, filterText, sortByOrg, treeData]);
    
    // Lấy danh sách đơn vị duy nhất từ entries
    const uniqueOrgs = React.useMemo(() => {
        const orgIds = [...new Set(customers.map(e => e.organization_id).filter(Boolean))];
        return orgIds.map(id => {
            const org = treeData.find(c => Number(c.id) === Number(id));
            return {
            id,
            name: org?.title || 'Không xác định'
            };
        }).sort((a, b) => a.name.localeCompare(b.name, 'vi'));
    }, [customers, treeData]);

    const handleSubmit = async (values) => {
        try {
            setSubmitting(true);

            if (!values.name?.trim()) {
                message.error("Vui lòng nhập tên chiến dịch");
                return;
            }

            if (!values.dateRange || values.dateRange.length !== 2) {
                message.error("Vui lòng chọn thời gian bắt đầu và kết thúc");
                return;
            }

            if (customers.length === 0) {
                message.error("Vui lòng thêm ít nhất một khách hàng");
                return;
            }

            // const customer_fees = customers.map(customer => {
            //     if (!customer.acc_payment_id) {
            //         throw new Error(`Vui lòng chọn tài khoản thanh toán cho khách hàng: ${customer.name}`);
            //     }

            //     return {
            //         id: customer.id || null,
            //         customer_id: customer.customer_id,
            //         organization_id: Number(customer.organization_id) || null,
            //         acc_payment_id: customer.acc_payment_id
            //     };
            // });

            const customerData = form.getFieldValue("customerData") || {};
            const customer_fees = customers.map(customer => {
                const accPaymentId = customerData[customer.customer_id]?.acc_payment_id || customer.acc_payment_id;
                if (!accPaymentId) {
                    throw new Error(`Vui lòng chọn tài khoản thanh toán cho khách hàng: ${customer.customer_name}`);
                }

                return {
                    id: customer.id || null,
                    customer_id: customer.customer_id,
                    organization_id: Number(customer.organization_id) || null,
                    acc_payment_id: accPaymentId
                };
            });

            const campainUpdateData = campainHelpers.formatCampainData({
                name: values.name.trim(),
                start_date: values.dateRange[0].format("YYYY-MM-DD"),
                end_date: values.dateRange[1].format("YYYY-MM-DD"),
                status: values.status || "PLANNED",
                customer_fees,
                is_send_zns: values.is_send_zns ?? false,
                send_schedule_at: valueSendScheduleAt ? dayjs(valueSendScheduleAt).format("HH:mm:ss") : null,
                before_date: values.before_date ?? null,
                after_date: values.after_date ?? null,
                content: values.content ?? null,
                zns_oa_name: values.zns_oa_name ?? null,
                zns_template_id: values.zns_template_id ?? null,
            });

            const validation = campainHelpers.validateCampainData(campainUpdateData);
            if (!validation.isValid) {
                message.error("Dữ liệu không hợp lệ: " + Object.values(validation.errors)[0]);
                return;
            }

            await campainAPIWithErrorHandling.updateCampain(id, campainUpdateData);
            message.success("Cập nhật chiến dịch thành công!");
            navigate(`/campains`);

        } catch (error) {
            message.error("Lỗi khi cập nhật chiến dịch: " + error.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => {
        navigate("/campains");
        // navigate(`/campains/information/${id}`);
    };



    const handleAddCustomer = () => {
        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc chắn muốn chuyển trang tạo khách hàng mới không?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: () => {
                navigate("/store-customer");
            },
        });
    };

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen p-6 flex items-center justify-center"
                style={{
                display: "flex",
                justifyContent: "center",
                height: "80vh",
                alignItems: "center",
                }}>
                <Spin size="large" tip="Đang tải dữ liệu chiến dịch..." />
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            {/* <Card className="shadow-lg max-w-7xl mx-auto"> */}
            <Card className="shadow-lg mx-auto">
                <div className="mb-6">
                    <Breadcrumb
                        separator=">"
                        className="mb-4"
                        items={[
                            { href: "/", title: <><HomeOutlined /> Trang chủ</> },
                            { href: "/campains", title: <><DollarOutlined /> Chiến dịch</> },
                            { title: <>Cập nhật: {campainData?.name}</> },
                        ]}
                    />
                </div>

                <Divider />

                <Spin spinning={submitting} tip="Đang cập nhật thông tin...">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        requiredMark="optional"
                    >
                        {/* Basic Information */}
                        <Row gutter={[24, 16]}>
                            <Col xs={24} lg={16}>
                                <Card
                                    title={
                                        <span className="text-blue-600">
                                            <CalendarOutlined className="mr-2" />
                                            Thông tin chiến dịch
                                        </span>
                                    }
                                    className="h-full shadow-sm border-blue-200"
                                >
                                    <Row gutter={16}>
                                        <Col xs={24} md={24}>
                                            <Form.Item
                                                name="name"
                                                label={<span className="font-medium">Tên chiến dịch</span>}
                                                rules={[
                                                    { required: true, message: "Vui lòng nhập tên chiến dịch" },
                                                    { max: 200, message: "Tên không được vượt quá 200 ký tự" },
                                                ]}
                                            >
                                                <Input
                                                    placeholder="VD: chiến dịch tháng 11/2025"
                                                    className="rounded-lg"
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="dateRange"
                                                label={<span className="font-medium">Thời gian thu</span>}
                                                rules={[
                                                    { required: true, message: "Vui lòng chọn thời gian thu" }
                                                ]}
                                            >
                                                <RangePicker
                                                    className="w-full rounded-lg"
                                                    format="DD/MM/YYYY"
                                                    placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
                                                    onChange={handleDateRangeChange}
                                                    style={{ width: "100%" }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                            name="invoice_cnf_id"
                                            label={<span className="font-medium">T/K phát hành hóa đơn</span>}
                                            rules={[
                                                { required: false, message: "Vui lòng chọn T/K phát hành hóa đơn" }
                                            ]}
                                            >
                                            <Select
                                                style={{ width: "100%" }}
                                                placeholder="Chọn tài khoản"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={invoiceCnfs.map((item) => ({
                                                    value: item.id,
                                                    label: item.name,
                                                }))}
                                            />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="zns_oa_name"
                                                label={<span className="font-medium">Tên OA Zalo</span>}
                                            >
                                                <Input
                                                    placeholder="Nhập tên Official Account"
                                                    className="rounded-lg"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="zns_template_id"
                                                label={<span className="font-medium">Template ID</span>}
                                            >
                                                <Input
                                                    placeholder="Nhập ID template ZNS"
                                                    className="rounded-lg"
                                                />
                                            </Form.Item>
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <Form.Item
                                                name="status"
                                                label={<span className="font-medium">Trạng thái</span>}
                                                rules={[{ required: true, message: "Vui lòng chọn trạng thái" }]}
                                            >
                                                <Select
                                                    placeholder="Chọn trạng thái"
                                                    options={[
                                                        { label: "Nháp", value: "PLANNED" },
                                                        { label: "Đang hoạt động", value: "ONGOING" },
                                                    ]}
                                                />
                                            </Form.Item>
                                        </Col>

                                    </Row>
                                </Card>
                            </Col>

                            <Col xs={24} lg={8}>
                                <Card
                                    title={
                                        <span className="text-green-600">
                                            <EnvironmentOutlined className="mr-2" />
                                            Cấu hình gửi tin
                                        </span>
                                    }
                                    className="h-full shadow-sm border-green-200"
                                >
                                    <Form.Item
                                        name="is_send_zns"
                                        label={<span className="font-medium">Tin thông báo</span>}
                                        valuePropName="checked"
                                    >
                                        <Switch checkedChildren="Bật" unCheckedChildren="Tắt" defaultChecked />
                                    </Form.Item>
                                    
                                    <Form.Item
                                        name="send_schedule_at"
                                        label={<span className="font-medium">Giờ gửi</span>}
                                    >
                                        <TimePicker
                                            style={{ width: "100%" }}
                                            placeholder="Chọn giờ gửi"
                                            onChange={onChangeSendScheduleAt}
                                            format="HH:mm"
                                            disabled={!isSendZns}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="before_date"
                                        label={<span className="font-medium">Trước hạn</span>}
                                        rules={[{ type: "number", min: 0, message: "≥ 0" }]}
                                    >
                                        <InputNumber
                                            min={0}
                                            placeholder="Số ngày"
                                            style={{ width: "100%" }}
                                            addonAfter="ngày"
                                            disabled={!isSendZns}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="after_date"
                                        label={<span className="font-medium">Sau hạn</span>}
                                        rules={[{ type: "number", min: 1, message: "≥ 1" }]}
                                    >
                                        <InputNumber
                                            min={1}
                                            placeholder="Số ngày"
                                            style={{ width: "100%" }}
                                            addonAfter="ngày"
                                            disabled={!isSendZns}
                                        />
                                    </Form.Item>

                                    <Alert
                                        message="Tin nhắn từ 22:00-06:00 sẽ được gửi vào sáng hôm sau"
                                        type="info"
                                        showIcon
                                        icon={<ExclamationCircleOutlined />}
                                        style={{ fontSize: 12 }}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        {/* ZNS Configuration */}
                        <Card
                            className="mt-6 shadow-sm"
                            title={
                                <span className="text-purple-600">
                                    <MessageOutlined className="mr-2" />
                                    Nội dung
                                </span>
                            }
                        >
                            <Row gutter={16}>
                                <Col xs={24}>
                                    <Form.Item
                                        name="content"
                                        label={<span className="font-medium">Nội dung thông báo</span>}
                                    >

                                        <SlateEditor
                                            value={contentValue}
                                            onChange={(jsonString, htmlString) => {
                                                // // jsonString: để lưu vào DB
                                                // // htmlString: để hiển thị preview hoặc gửi email
                                                // setContentValue(jsonString);
                                                // form.setFieldValue("content", jsonString);
                                                setContentValue(htmlString);
                                                form.setFieldValue("content", htmlString); // CHỈ lưu HTML
                                            }}
                                            placeholder="Soạn nội dung thông báo gửi cho khách hàng…"
                                        />

                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Customer Selection */}
                        <Row gutter={[24, 16]}>
                            <Col xs={24} lg={16}>
                                <Card
                                    className="mt-6 shadow-sm"
                                    title={
                                        <span className="text-purple-600">
                                            <UserOutlined className="mr-2" />
                                            Quản lý khách hàng
                                        </span>
                                    }
                                >
                                    
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                    <Row gutter={[12, 12]}>
                                        <Col xs={24} sm={12} md={8} lg={12} xl={12}>
                                            <TreeSelect
                                                treeDataSimpleMode
                                                style={{ width: '100%' }}
                                                value={valueOrg}
                                                placeholder="Chọn đơn vị"
                                                onChange={onChange}
                                                treeData={treeData}
                                                showSearch
                                                filterTreeNode={(input, item) =>
                                                    item.title.toLowerCase().includes(input.toLowerCase())
                                                }
                                                allowClear
                                                treeDefaultExpandAll={false}
                                            />
                                        </Col>

                                        <Col xs={24} sm={12} md={8} lg={12} xl={12}>
                                            <Select
                                                style={{ width: '100%' }}
                                                placeholder="Lọc theo nhóm"
                                                value={selectedGroup}
                                                onChange={handleGroupChange}
                                                disabled={!valueOrg || availableGroups.length === 0 || loadingGroup}
                                                loading={loadingGroup}
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={availableGroups.map(group => ({
                                                    value: group.id,
                                                    label: group.name,
                                                }))}
                                            />
                                        </Col>

                                        <Col xs={24} sm={24} md={8} lg={24} xl={24}>
                                            <Select
                                                placeholder="Chọn khách hàng"
                                                style={{ width: '100%' }}
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                loading={loadingCus}
                                                disabled={loadingCus}
                                                onSelect={addCustomer}
                                                value={undefined}
                                                options={availableCustomers
                                                    .filter(customer => !customers.some(c => c.customer_id === customer.id))
                                                    .map(customer => ({
                                                        value: customer.id,
                                                        label: `${customer.name} - ${customer.phone}`,
                                                    }))}
                                            />
                                        </Col>
                                        </Row>
                                        
                                        <Row gutter={[12, 12]}>
                                            <Col xs={24} sm={12} md={12} lg={12}>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={addAllCustomers}
                                                    disabled={
                                                        availableCustomers.length === 0 ||
                                                        availableCustomers.every((c) =>
                                                            customers.some((x) => x.customer_id === c.id)
                                                        )
                                                    }
                                                    style={{ width:"100%" }}
                                                >
                                                    Thêm tất cả
                                                </Button>
                                            </Col>
                                            <Col xs={24} sm={12} md={12} lg={12}>

                                                <Button
                                                    type="dashed"
                                                    icon={<PlusOutlined />}
                                                    onClick={handleAddCustomer}
                                                    style={{ width:"100%" }}
                                                >
                                                    Tạo khách hàng mới
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Space>
                                </Card>
                            </Col>
                            <Col xs={24} lg={8}>
                                <Card 
                                    className="mt-6 shadow-sm"
                                    style={{ 
                                        border: !selectedAccount ? '2px dashed #E5065C' : '1px solid #CBD5E1'
                                    }}
                                >
                                {!selectedAccount && (
                                    <Alert
                                        message="Vui lòng chọn tài khoản thanh toán trước khi thêm khách hàng"
                                        type="warning"
                                        showIcon
                                        style={{ fontSize: 12, marginBottom: 14 }}
                                    />
                                )}
                                <PaymentAccountSelector
                                    accounts={accounts}
                                    onSelect={handleSelectAccount}
                                    selectedAccountId={selectedAccount?.id}
                                />
                                </Card>
                            </Col>
                        </Row>

                        <Card
                            className="mt-6 shadow-sm"
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span className="text-orange-600">
                                        <DollarOutlined className="mr-2" />
                                        Danh sách khách hàng
                                    </span>
                                    <Space wrap>
                                        <Text type="secondary" style={{ fontSize: 14 }}>
                                            Hiển thị: <strong>{tableDataSource.filter(r => !r.isHeader).length}</strong> / <strong>{customers.length}</strong> khách hàng
                                        </Text>
                                    </Space>
                                </div>
                            }
                        >
                            {customers.length > 0 && (
                                <div style={{ 
                                    marginBottom: 16, 
                                    padding: 12, 
                                    backgroundColor: '#F8FAFC', 
                                    borderRadius: 8,
                                    border: '1px solid #E2E8F0'
                                }}>
                                    <Row gutter={[12, 12]}>
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Input
                                            placeholder="Tìm theo tên, SĐT, tài sản..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            allowClear
                                            prefix={<SearchOutlined style={{ color: '#94A3B8' }} />}
                                            />
                                        </Col>
                                        
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Select
                                            style={{ width: '100%' }}
                                            placeholder="Lọc theo đơn vị"
                                            value={filterOrgId}
                                            onChange={setFilterOrgId}
                                            allowClear
                                            showSearch
                                            filterOption={(input, option) =>
                                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                            }
                                            options={[
                                                { value: null, label: 'Tất cả đơn vị' },
                                                ...uniqueOrgs.map(org => ({
                                                value: org.id,
                                                label: org.name
                                                }))
                                            ]}
                                            />
                                        </Col>
                                                              
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Button
                                                type={sortByOrg ? "primary" : "default"}
                                                icon={<EnvironmentOutlined />}
                                                onClick={() => setSortByOrg(!sortByOrg)}
                                                style={{ width: '100%' }}
                                            >
                                                {sortByOrg ? "Đang nhóm" : "Nhóm theo đơn vị"}
                                            </Button>
                                        </Col>
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            <Button
                                                style={{ width: '100%' }}
                                                onClick={() => {
                                                    setFilterText("");
                                                    setFilterOrgId(null);
                                                    setSortByOrg(true);
                                                }}
                                                disabled={!filterText && !filterOrgId && sortByOrg}
                                            >
                                                Xóa bộ lọc
                                            </Button>
                                        </Col>
                                        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                                            {(filterText || filterOrgId) && (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    <FilterOutlined /> Đang lọc
                                                </Text>
                                            )}
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* {customers.length > 0 ? ( */}
                            {tableDataSource.length > 0 ? (
                                (() => {
                                    const totalWidth = columns.reduce((sum, col) => {
                                        const width = typeof col.width === 'string'
                                            ? parseInt(col.width) || 150
                                            : (col.width || 150);
                                        return sum + width;
                                    }, 0);

                                    const rowHeight = 70;
                                    const maxHeight = 500;
                                    const listHeight = Math.min(tableDataSource.length * rowHeight, maxHeight);

                                    return (
                                        <div style={{
                                            border: '1px solid #F1F5F9',
                                            borderRadius: '8px',
                                            overflow: 'auto',
                                            maxWidth: '100%'
                                        }}>
                                            <div style={{ minWidth: totalWidth + 50 }}>
                                                {/* Header */}
                                                <div style={{
                                                    display: 'flex',
                                                    borderBottom: '2px solid #E2E8F0',
                                                    backgroundColor: '#dbdbdb',
                                                    padding: '12px 8px',
                                                    fontWeight: 600,
                                                    position: 'sticky',
                                                    top: 0,
                                                    zIndex: 10
                                                }}>
                                                    {columns.map((col, idx) => (
                                                        <div
                                                            key={idx}
                                                            style={{
                                                                width: typeof col.width === 'string' ? col.width : `${col.width}px`,
                                                                minWidth: typeof col.width === 'string' ? col.width : `${col.width}px`,
                                                                flexShrink: 0,
                                                                textAlign: col.align || 'left',
                                                                padding: '0 8px',
                                                                borderRight: idx !== columns.length - 1 ? '1px solid #E2E8F0' : 'none',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            {typeof col.title === 'object' ? col.title : col.title}
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Body with Virtual Scroll */}
                                                <List
                                                    height={listHeight}
                                                    itemCount={tableDataSource.length}
                                                    itemSize={70}
                                                    width="100%"
                                                >
                                                    {({ index, style }) => {
                                                        const record = tableDataSource[index];
                                                        // Nếu là header row
                                                        if (record.isHeader) {
                                                            return (
                                                                <div
                                                                    style={{
                                                                        ...style,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        padding: '8px 16px',
                                                                        backgroundColor: '#F0F6FD',
                                                                        borderBottom: '2px solid #0070D7',
                                                                        fontWeight: 600,
                                                                        color: '#0070D7'
                                                                    }}
                                                                >
                                                                    <EnvironmentOutlined style={{ marginRight: 8 }} />
                                                                    {record.orgName} ({record.itemCount} khách hàng)
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <div
                                                                style={{
                                                                    ...style,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    borderBottom: '1px solid #F1F5F9',
                                                                    padding: '8px',
                                                                    backgroundColor: index % 2 === 0 ? '#fff' : '#F8FAFC'
                                                                }}
                                                            >
                                                                {columns.map((col, colIdx) => (
                                                                    <div
                                                                        key={colIdx}
                                                                        style={{
                                                                            width: typeof col.width === 'string' ? col.width : `${col.width}px`,
                                                                            minWidth: typeof col.width === 'string' ? col.width : `${col.width}px`,
                                                                            flexShrink: 0,
                                                                            padding: '0 8px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            ...(col.align === 'right' && {
                                                                                justifyContent: 'flex-end',
                                                                            }),
                                                                            ...(col.align === 'left' && {
                                                                                justifyContent: 'flex-start',
                                                                            }),
                                                                            ...(col.align === 'center' && {
                                                                                justifyContent: 'center',
                                                                            })
                                                                        }}
                                                                    >
                                                                        {col.render ? col.render(record[col.dataIndex], record, index) : record[col.dataIndex]}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    }}
                                                </List>
                                            </div>
                                        </div>
                                    );
                                })()
                            ) : (
                                <div className="text-center py-12">
                                    <UserOutlined style={{ fontSize: 48, color: '#CBD5E1', marginBottom: 16 }} />
                                    <div>
                                        <Text type="secondary" style={{ fontSize: 16 }}>
                                            Chưa có khách hàng nào
                                        </Text>
                                    </div>
                                    <div>
                                        <Text type="secondary">
                                            Vui lòng chọn đơn vị và thêm khách hàng để bắt đầu
                                        </Text>
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card className="mt-6 shadow-sm bg-gradient-to-r from-blue-50 to-green-50">
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Button
                                        icon={<ArrowLeftOutlined />}
                                        onClick={handleBack}
                                        size="default"
                                        className="shadow-sm"
                                    >
                                        Quay lại
                                    </Button>
                                </Col>
                                <Col>
                                    <Button
                                        type="primary"
                                        size="default"
                                        htmlType="submit"
                                        loading={submitting}
                                        icon={<SaveOutlined />}
                                        className="min-w-40 rounded-lg shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 border-none"
                                    >
                                        {submitting ? "Đang cập nhật..." : "Cập nhật thông tin"}
                                    </Button>
                                </Col>
                            </Row>
                        </Card>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
}